import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Meeting {
  id: string;
  join_url: string | null;
  title: string | null;
  description: string | null;
  topic: string | null;
  meeting_datetime: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface MeetingFormData {
  title: string;
  meeting_datetime: string;
  description?: string;
  join_url?: string;
  topic?: string;
  /** Set status='active' on insert. Otherwise defaults to 'queued'. */
  activate?: boolean;
}

export function useActiveMeeting() {
  return useQuery({
    queryKey: ['meetings', 'active'],
    staleTime: 30_000,
    queryFn: async (): Promise<Meeting | null> => {
      const { data, error } = await supabase
        .from('meetings')
        .select('*')
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as Meeting | null;
    },
  });
}

export function useAllMeetings() {
  return useQuery({
    queryKey: ['meetings', 'all'],
    staleTime: 30_000,
    queryFn: async (): Promise<Meeting[]> => {
      const { data, error } = await supabase
        .from('meetings')
        .select('*')
        .order('meeting_datetime', { ascending: true });

      if (error) throw error;
      return (data as Meeting[]) || [];
    },
  });
}

export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: MeetingFormData) => {
      // The activate path goes through a SQL function so the demote of
      // the existing active meeting + insert of the new one happen in a
      // single transaction. Two parallel "make active" clicks can't end
      // up with two active rows or no active rows.
      if (formData.activate) {
        // Type cast: the auto-generated Database types haven't been
        // regenerated since the create_active_meeting RPC was added.
        // Once `supabase gen types typescript` runs, this can drop.
        const { error } = await (supabase.rpc as unknown as (
          fn: 'create_active_meeting',
          args: {
            p_title: string;
            p_meeting_datetime: string;
            p_description: string | null;
            p_join_url: string | null;
            p_topic: string | null;
          },
        ) => Promise<{ error: Error | null }>)('create_active_meeting', {
          p_title: formData.title,
          p_meeting_datetime: formData.meeting_datetime,
          p_description: formData.description ?? null,
          p_join_url: formData.join_url ?? null,
          p_topic: formData.topic ?? null,
        });
        if (error) throw error;
        return;
      }

      const { error } = await supabase.from('meetings').insert({
        join_url: formData.join_url || null,
        title: formData.title,
        description: formData.description || null,
        topic: formData.topic || null,
        meeting_datetime: formData.meeting_datetime,
        status: 'queued',
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}

/**
 * Patch any subset of a meeting's editable fields. Used by the edit-meeting
 * dialog so admins can add a join URL, fix a typo in the title, change the
 * date, or update the topic line on an event that already exists. Status
 * is intentionally not editable here — that flow has its own RPC
 * (create_active_meeting / useUpdateMeetingStatus) so we don't accidentally
 * break the "exactly one active meeting" invariant from a generic update.
 */
export interface MeetingUpdateData {
  id: string;
  title?: string;
  meeting_datetime?: string;
  description?: string | null;
  join_url?: string | null;
  topic?: string | null;
}

export function useUpdateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MeetingUpdateData) => {
      const { id, ...patch } = input;
      // Build the update payload only with provided keys so we never
      // accidentally null-out a field the caller didn't intend to touch.
      const update: Record<string, string | null> = {};
      if (patch.title !== undefined) update.title = patch.title;
      if (patch.meeting_datetime !== undefined)
        update.meeting_datetime = patch.meeting_datetime;
      if (patch.description !== undefined)
        update.description = patch.description;
      if (patch.join_url !== undefined) update.join_url = patch.join_url;
      if (patch.topic !== undefined) update.topic = patch.topic;

      const { error } = await supabase
        .from('meetings')
        .update(update)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}

export function useUpdateMeetingStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('meetings')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}

export function useDeleteMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('meetings').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meetings'] }),
  });
}
